import { alertDefinition, fillTemplate } from '@plancia/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Info } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cx } from '@/components/ui/cx';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/states';
import { isApiError } from '@/lib/api';
import { type ShipmentDetail, shipmentKeys, shipmentsApi } from '@/lib/api/shipments';
import { meQuery } from '@/lib/auth/session';
import { clockTime, dayMonth, dayMonthTime } from '@/lib/format/time';
import { useNow } from '@/lib/hooks/use-now';
import { useShipment } from '../context';
import { useDeliveries } from '../header-lines';
import {
  buildEntries,
  clockOf,
  countByKind,
  type EntryKind,
  evidenceOf,
  HISTORY_FILTERS,
  type HistoryEntry,
  type HistoryFilter,
} from './model';
import './history.css';

// S07 · History (07 §3): one vertical log of what happened to the shipment and
// why — state changes, alerts, notifications, people's actions, Dokless's
// requests and the deliveries — with «Perché è chiusa» on top when closed.

export interface HistorySearch {
  kind?: Exclude<HistoryFilter, 'all'>;
}

export function validateHistorySearch(search: Record<string, unknown>): HistorySearch {
  const k = search.kind;
  return typeof k === 'string' && k !== 'all' && (HISTORY_FILTERS as readonly string[]).includes(k)
    ? { kind: k as Exclude<HistoryFilter, 'all'> }
    : {};
}

const GLYPH: Record<EntryKind, string> = {
  state: '⇢',
  alert: '▲',
  notification: '✉',
  person: '✋',
  dokless: 'D',
  delivery: '↗',
  activity: '·',
};

export function HistoryTab() {
  const shipment = useShipment();
  const { t } = useTranslation('shipment-history');
  const query = useQuery({
    queryKey: shipmentKeys.part(shipment.id, 'history'),
    queryFn: () => shipmentsApi.history(shipment.id),
  });
  const deliveries = useDeliveries(shipment.id);
  const search = useSearch({ strict: false }) as HistorySearch;
  const navigate = useNavigate();
  const filter: HistoryFilter = search.kind ?? 'all';
  const pick = (kind: HistoryFilter) =>
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => {
        const { kind: _drop, ...rest } = prev;
        return kind === 'all' ? rest : { ...rest, kind };
      },
      replace: true,
    } as never);

  if (query.isPending) return <HistorySkeleton />;
  if (query.isError)
    return (
      <div className="hs" data-testid="tab-history">
        <ErrorState
          title={t('error.title')}
          body={t('error.body', { status: isApiError(query.error) ? query.error.status : '—' })}
          onRetry={() => void query.refetch()}
        />
      </div>
    );

  const entries = buildEntries(query.data.items, shipment, deliveries.data?.payloads ?? []);
  const n = countByKind(entries);
  const shown = filter === 'all' ? entries : entries.filter((e) => e.kind === filter);
  const filters = HISTORY_FILTERS.filter((k) => k !== 'activity' || n.activity > 0 || filter === 'activity');

  return (
    <div className="hs" data-testid="tab-history">
      {shipment.status === 'closed' ? (
        <ClosedBecause
          shipment={shipment}
          fallback={query.data.closed_because}
          closed={query.data.closed ?? null}
        />
      ) : null}
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        {/* biome-ignore lint/a11y/useSemanticElements: a set of toggle buttons (the boards' segmented control), not a form fieldset */}
        <div className="seg" role="group" aria-label={t('filters.kind')}>
          {filters.map((k) => (
            <button
              key={k}
              type="button"
              className={cx(filter === k && 'on')}
              aria-pressed={filter === k}
              title={t('filters.count', { count: n[k] })}
              onClick={() => pick(k)}
            >
              {t(`filter.${k}`)}
            </button>
          ))}
        </div>
        <span className="sub" style={{ marginLeft: 'auto' }}>
          {t('audited')}
        </span>
      </div>
      {entries.length === 0 ? (
        <EmptyState title={t('empty.title')} body={t('empty.body')} />
      ) : shown.length === 0 ? (
        <EmptyState
          title={t('filtered.title', { kind: t(`filter.${filter}`) })}
          action={
            <button type="button" className="btn sm" onClick={() => pick('all')}>
              {t('filtered.all')}
            </button>
          }
        />
      ) : (
        <section className="card hs-log" aria-label={t('aria')}>
          <ol>
            {shown.map((e) => (
              <Entry key={e.key} entry={e} shipment={shipment} />
            ))}
          </ol>
        </section>
      )}
    </div>
  );
}

/** «Perché è chiusa»: the closing transition's reason and the closing rule; else the API's line. */
function ClosedBecause({
  shipment: s,
  fallback,
  closed,
}: {
  shipment: ShipmentDetail;
  fallback: string | null;
  closed: { reason: string | null; at: string | null; rule: string | null } | null;
}) {
  const { t, i18n } = useTranslation('shipment-history');
  const closing = [...s.transitions]
    .filter((x) => x.to_status === 'closed')
    .sort((a, b) => b.at.localeCompare(a.at))[0];
  // The rule's own text is the domain's (English): in another language the API's parts are written instead.
  const at = closed?.at ? new Date(closed.at) : null;
  const local =
    closed?.reason && i18n.language !== 'en'
      ? t(`closedWhy.${closed.reason}`, {
          date: at && Number.isFinite(at.getTime()) ? dayMonth(at) : '—',
          defaultValue: '',
        })
      : '';
  const reason = local || closing?.reason || closed?.rule || fallback || t('closed.unknown');
  return (
    <div className="card hs-closed" data-testid="closed-because">
      <Icon as={Info} className="c-pl" style={{ marginTop: 1 }} />
      <div>
        <strong>{t('closed.label')}</strong> {sentence(reason)}{' '}
        {s.closed_reason ? (
          <span className="sub">
            {t('closed.rule', { rule: t(`rule.${s.closed_reason}`, { defaultValue: s.closed_reason }) })} (
            <Link to="/settings" className="sub hs-rule">
              {t('closed.where')}
            </Link>
            ).
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** A reason as a sentence: the final full stop when it has none. */
const sentence = (text: string) => (/[.!?…»]$/.test(text.trim()) ? text.trim() : `${text.trim()}.`);

function useWho() {
  const me = useQuery(meQuery);
  return (who: string | null) => {
    if (!who) return null;
    if (me.data && who === me.data.id) return me.data.name.split(/\s+/)[0] ?? me.data.name;
    return who;
  };
}

function Entry({ entry: e, shipment }: { entry: HistoryEntry; shipment: ShipmentDetail }) {
  const { t, i18n } = useTranslation('shipment-history');
  const { t: tc } = useTranslation();
  const now = new Date(useNow(60_000));
  const whoName = useWho();
  const today = dayMonth(e.at) === dayMonth(now) && e.at.getFullYear() === now.getFullYear();
  const lang = i18n.language === 'en' ? 'en' : 'it';
  const status = (s: string | null) => (s ? tc(`shipmentStatus.${s}`, { defaultValue: s }) : '—');

  let title: ReactNode = null;
  let body: ReactNode = null;
  let evidence: ReactNode[] = [];

  switch (e.kind) {
    case 'state': {
      title = (
        <>
          {status(e.from)} → {status(e.to)}
        </>
      );
      if (e.reason)
        body =
          e.to === 'closed' && shipment.closed_reason
            ? t('state.closing', {
                rule: t(`rule.${shipment.closed_reason}`, { defaultValue: shipment.closed_reason }),
                reason: sentence(e.reason),
              })
            : t('state.reason', { reason: sentence(e.reason) });
      evidence = evidenceOf(e.evidence, shipment.events).map((ev) =>
        ev.kind === 'read' && ev.readId ? (
          <Link
            key={`r-${ev.readId}`}
            className="chip"
            to="/shipments/$id/reads"
            params={{ id: shipment.id }}
            search={{ read: ev.readId } as never}
          >
            {t('evidence.read', { at: ev.at ? readWhen(ev.at, now) : '' })}
          </Link>
        ) : ev.kind === 'ais' ? (
          <Link key={`a-${ev.clock}`} className="chip" to="/shipments/$id/route" params={{ id: shipment.id }}>
            {t('evidence.ais', { at: clockOf(ev.clock ?? '') })}
          </Link>
        ) : (
          <Link
            key={`e-${ev.eventId}`}
            className="chip"
            to="/shipments/$id/timeline"
            params={{ id: shipment.id }}
          >
            {t('evidence.event', { at: ev.at ? dayMonthTime(ev.at) : '—' })}
          </Link>
        ),
      );
      break;
    }
    case 'alert': {
      const def = alertDefinition(e.code);
      const name = def ? fillTemplate(def.title[lang], e.params) : e.code;
      title = t(`alert.${e.action}`, { title: name });
      body =
        e.action === 'closed'
          ? e.closedReason
            ? t('alert.closedBecause', { reason: e.closedReason })
            : null
          : def
            ? fillTemplate(def.message[lang], e.params)
            : null;
      break;
    }
    case 'notification': {
      const what = t(`notification.kind.${e.notification}`, { defaultValue: e.notification });
      const channel = e.channel === 'telegram' || !e.channel ? 'Telegram' : e.channel;
      title =
        e.state === 'sent'
          ? t('notification.sent', { channel, what })
          : e.state === 'failed'
            ? t('notification.failed', { channel, what })
            : e.state === 'pending'
              ? t('notification.pending', { what })
              : e.reason === 'quiet_hours'
                ? t('notification.postponed', { what })
                : t('notification.notSent', { what });
      if ((e.state === 'sent' || e.state === 'failed') && e.text) body = `«${e.text.split('\n')[0]}»`;
      if (e.state === 'suppressed' && e.reason)
        body = t('notification.because', {
          reason: t(`notification.reason.${e.reason}`, { defaultValue: e.reason }),
        });
      break;
    }
    case 'person': {
      const action = e.action.replace(/^shipment\./, '');
      const who = e.whoName?.split(/\s+/)[0] ?? whoName(e.who) ?? t('person.someone');
      const change = personChange(action, e.before, e.after, t);
      title = `${who} · ${t(`person.action.${action}`, { defaultValue: action })}${change ? `: ${change}` : ''}`;
      const reason =
        e.reason ?? (typeof e.after.reason === 'string' && e.after.reason ? e.after.reason : null);
      body = reason ? t('person.reason', { reason }) : null;
      break;
    }
    case 'dokless':
    case 'activity':
      title =
        e.fingerprint !== undefined ? t('dokless.request', { fingerprint: e.fingerprint ?? '—' }) : e.summary;
      if (e.readId)
        evidence = [
          <Link
            key="read"
            className="chip"
            to="/shipments/$id/reads"
            params={{ id: shipment.id }}
            search={{ read: e.readId } as never}
          >
            {t('evidence.readPlain')}
          </Link>,
        ];
      break;
    case 'delivery': {
      title = t(`delivery.${e.state}`, { rev: e.revision });
      const parts = [
        t(`delivery.kind.${e.payloadKind}`, { defaultValue: e.payloadKind }),
        e.status !== null ? t('delivery.status', { status: e.status }) : null,
      ].filter(Boolean);
      body = `${parts.join(', ')}.${e.nextCheckAt ? ` ${t('delivery.next', { at: clockTime(new Date(e.nextCheckAt)) })}` : ''}`;
      evidence = [
        <Link key="rev" className="chip" to="/shipments/$id/deliveries" params={{ id: shipment.id }}>
          {t('evidence.rev', { rev: e.revision })}
        </Link>,
      ];
      break;
    }
  }

  return (
    <li className="hs-item" data-testid="history-item" data-kind={e.kind}>
      <div style={{ paddingTop: 3, paddingBottom: 12 }}>
        <div className="mono">{clockTime(e.at)}</div>
        <div className="sub">{today ? t('today') : dayMonth(e.at)}</div>
      </div>
      <div className="hs-rail">
        <span className="hs-kind" role="img" aria-label={t(`kind.${e.kind}`)}>
          {GLYPH[e.kind]}
        </span>
      </div>
      <div style={{ paddingBottom: 16, minWidth: 0 }}>
        <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
          <span className="tag">{t(`kind.${e.kind}`)}</span>
          <strong>{title}</strong>
        </div>
        {body ? (
          <div className="m2" style={{ marginTop: 3 }}>
            {body}
          </div>
        ) : null}
        {evidence.length > 0 ? (
          <div className="flex flex-wrap" style={{ gap: 6, marginTop: 6 }}>
            <span className="sub">{t('evidence.label')}</span>
            {evidence}
          </div>
        ) : null}
      </div>
    </li>
  );
}

const readWhen = (at: Date, now: Date) => (dayMonth(at) === dayMonth(now) ? clockTime(at) : dayMonthTime(at));

/** «modello → auto» for a read-mode change and the like; null when the action has no before/after to show. */
function personChange(
  action: string,
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | null {
  if (action === 'set_read_mode') {
    const mode = (m: unknown) =>
      t(`readMode.${typeof m === 'string' ? m : 'auto'}`, { defaultValue: String(m) });
    return `${mode(before.read_mode ?? 'auto')} → ${mode(after.read_mode)}`;
  }
  if (action === 'change_carrier' && typeof after.carrier === 'string') return after.carrier;
  return null;
}

function HistorySkeleton() {
  const { t } = useTranslation('shipment-history');
  return (
    <div className="hs" data-testid="tab-history">
      <Skeleton width={420} height={32} />
      <section className="card hs-log" role="status" aria-busy="true" aria-label={t('loading')}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="hs-item">
            <div className="flex flex-col" style={{ gap: 6, paddingTop: 3 }}>
              <Skeleton width={40} height={12} />
              <Skeleton width={34} height={10} />
            </div>
            <div className="hs-rail">
              <Skeleton width={26} height={26} />
            </div>
            <div className="flex flex-col" style={{ gap: 8, paddingBottom: 16 }}>
              <Skeleton width="40%" height={14} />
              <Skeleton width="70%" height={10} />
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
